import React from 'react';
import { useHistory } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Button, Box } from '@mui/material';
import { useAuth } from '../../../context/auth';
import { USER_ACCESS_LEVEL } from '../../../utils/constants';

const primaryButtonSx = {
    background: '#FF6900',
    color: '#fff',
    borderRadius: '50px',
    textTransform: 'none',
    fontWeight: 700,
    px: 3,
    boxShadow: 'none',
    '&:hover': { background: '#c9541e' },
};

const secondaryButtonSx = {
    borderRadius: '50px',
    textTransform: 'none',
    fontWeight: 700,
    px: 1.5,
    lineHeight: 1.2,
};

/**
 * Two-step Publish confirmation flow, shared by the program dashboard and the
 * program list views:
 *  1. `stage === 'confirm'` — "Are you sure?" warning (publish is one-way / no unpublish).
 *  2. `stage === 'success'` — shown after a successful publish, offering next actions.
 * `stage === null` renders nothing (both dialogs closed).
 */
const PublishProgramDialogs = ({
    stage,
    publishing,
    onGoBack,
    onConfirmPublish,
    onGoToMaterials,
    onLinkToSchool,
    // Set ONLY when this publish fulfilled a school's learning-program request
    // (P1 Workspace, slide 155) — switches the success copy to the "linked to
    // (school)" confirmation. undefined = normal publish, unchanged copy.
    deliveredSchoolName = '',
    linkingToSchool = false,
}) => {
    const history = useHistory();
    const { authUser } = useAuth();
    // An LC Teacher has no schools: they link a program to their classes, from
    // My Classes (LA-TW-19Oct-04).
    const isTeacher = Number(authUser?.access_level) === USER_ACCESS_LEVEL.TEACHER_ACCESS_DQ_INSIDER_PORTAL;
    return (
        <>
            <Dialog open={stage === 'confirm'} onClose={onGoBack} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    Publish this program?
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ textAlign: 'center', color: '#555' }}>
                        Are you sure you want to publish this program? Once published, you cannot edit it anymore.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                        <Button variant="outlined" onClick={onGoBack} disabled={publishing} sx={secondaryButtonSx}>
                            Go Back
                        </Button>
                        <Button variant="contained" onClick={onConfirmPublish} disabled={publishing} sx={primaryButtonSx}>
                            {publishing ? 'Publishing...' : 'Publish'}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            <Dialog open={stage === 'success'} onClose={onGoToMaterials} maxWidth="xs" fullWidth>
                <DialogContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                    {deliveredSchoolName ? (
                        /* Bug report (slide 40): this used to say "now linked to X",
                           but publishing only DELIVERS and emails the school — the
                           owner still has to link it before the school can open it.
                           Copy below is the wording requested in that report. */
                        <>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                                Awesome!
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#555', mb: 3 }}>
                                Your learning program is now delivered to <strong>{deliveredSchoolName}</strong> —
                                they have been notified by email. However, you have to link it to their
                                account before they can access it.
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                                Great! Your program is now published and you can now distribute the programs to schools.
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#555', mb: 3 }}>
                                What would you like to do?
                            </Typography>
                        </>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            onClick={onGoToMaterials}
                            sx={{ borderRadius: '50px', textTransform: 'none', fontWeight: 700 }}
                        >
                            Go to Learning Program Materials
                        </Button>
                        <Button
                            variant="contained"
                            onClick={isTeacher ? () => history.push('/dq_pro/classes') : onLinkToSchool}
                            disabled={linkingToSchool}
                            sx={{ borderRadius: '50px', textTransform: 'none', fontWeight: 700 }}
                        >
                            {linkingToSchool ? 'Linking…' : (isTeacher ? 'Link Program to Class' : 'Link Program to School')}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default PublishProgramDialogs;
